import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import { ArrowLeftIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import ViewDocumentModal from "components/leads/LeadDetails/DocumentsTab/components/ViewDocumentModal";
import { downloadDocument } from "services/documentServices";
import { getAttachmentPreviewUrl } from "services/documentUploadService";
import { invalidateToolboxTalksCache } from "hooks/useToolboxTalks";
import { createToolboxTalk } from "services/whs/toolboxTalksService";
import { resolveWHSToken } from "services/whs/whsService";
import { DocumentKeyEnum } from "enum/DocumentKeyEnum";
import { useAuthContext } from "app/contexts/auth/context";
import { toast } from "sonner";
import sunnyHomesCrmLogo from "assets/images/SH-logo.png";
import { JWT_HOST_API } from "configs/auth.config";
import { isSunnyHomes } from "./whs-qr/InputFormatters";

// Shared WHS Modular Primitives
import { getTodayInputDate, getCurrentFormattedTime, stripHtml } from "./whs/shared/whsHelpers";
import WhsPageLayout from "./whs/shared/WhsPageLayout";
import WhsJobBanner from "./whs/shared/WhsJobBanner";
import WhsSuccessCountdown from "./whs/shared/WhsSuccessCountdown";
import useToolboxTalkLookups from "./whs/toolbox-talk/useToolboxTalkLookups";
import ToolboxTalkFormFields from "./whs/toolbox-talk/ToolboxTalkFormFields";

export default function WhsToolboxTalkPage() {
  const { token: paramToken } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: loggedInUser } = useAuthContext();

  const token =
    paramToken || location.state?.token || localStorage.getItem("whs_pending_token") || "";

  // Resolved Site & Company State
  const [jobData, setJobData] = useState(() => {
    if (location.state?.jobData) return location.state.jobData;
    try {
      const cached = localStorage.getItem("whs_job_data");
      if (cached) return JSON.parse(cached);
    } catch (e) {
      console.error("Failed to parse cached whs_job_data:", e);
    }
    return null;
  });

  const [companyId, setCompanyId] = useState(() => {
    return (
      location.state?.companyId ||
      location.state?.jobData?.companyId ||
      location.state?.jobData?.CompanyId ||
      (() => {
        try {
          const cached = localStorage.getItem("whs_job_data");
          if (cached) {
            const parsed = JSON.parse(cached);
            return parsed?.companyId || parsed?.CompanyId || "";
          }
        } catch {
          return "";
        }
        return "";
      })() ||
      localStorage.getItem("whs_company_id") ||
      loggedInUser?.companyId ||
      loggedInUser?.CompanyId ||
      ""
    );
  });
  const [companyLogo, setCompanyLogo] = useState(sunnyHomesCrmLogo);

  // Form States
  const [presenter, setPresenter] = useState("");
  const [talkDate, setTalkDate] = useState(getTodayInputDate());
  const [time, setTime] = useState(getCurrentFormattedTime(false));
  const [topic, setTopic] = useState("");
  const [discussionPoints, setDiscussionPoints] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("");
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [docFiles, setDocFiles] = useState([]);

  // Preview Document Modal State
  const [previewDoc, setPreviewDoc] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Submission & Success state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [submittedSummary, setSubmittedSummary] = useState(null);

  // Field Validation State
  const [errors, setErrors] = useState({});

  const dynamicFieldsRef = useRef(null);

  const isInternalUser = useMemo(() => {
    if (
      location.state?.isInternalUser === true ||
      location.state?.userForm?.isInternalUser === true ||
      localStorage.getItem("whs_is_internal_user") === "true"
    ) {
      return true;
    }
    if (localStorage.getItem("whs_username") && localStorage.getItem("authToken")) {
      return true;
    }
    if (
      loggedInUser &&
      (loggedInUser.isInternalUser ||
        loggedInUser.IsInternalUser ||
        loggedInUser.isInternalRole ||
        (!loggedInUser.externalCompanyId && isSunnyHomes(loggedInUser.companyName)))
    ) {
      return true;
    }
    if (
      localStorage.getItem("whs_is_internal_user") === "false" ||
      location.state?.userForm?.isInternalUser === false
    ) {
      return false;
    }
    return true;
  }, [location.state, loggedInUser]);

  // Set Company Logo
  useEffect(() => {
    try {
      const hostname = window.location.hostname;
      let folderName = hostname;
      if (hostname.includes("localhost") || hostname.includes("127.0.0.1")) {
        folderName = "localhost";
      }
      const apiHost = JWT_HOST_API?.endsWith("/") ? JWT_HOST_API : `${JWT_HOST_API}/`;
      setCompanyLogo(`${apiHost}CompanyLogos/${folderName}/main.png`);
    } catch (err) {
      console.error("Failed to set company logo from URL:", err);
    }
  }, []);

  // Resolve QR token & context
  useEffect(() => {
    let isMounted = true;
    const resolveContext = async () => {
      let initialJobData = jobData;
      if (!initialJobData) {
        try {
          const cached = localStorage.getItem("whs_job_data");
          if (cached) {
            initialJobData = JSON.parse(cached);
            if (isMounted) setJobData(initialJobData);
          }
        } catch (e) {
          console.error("Failed to parse cached whs_job_data:", e);
        }
      }

      if (token && !initialJobData) {
        try {
          const result = await resolveWHSToken(token);
          const resolved = result?.data || result;
          if (isMounted && resolved) {
            setJobData(resolved);
            initialJobData = resolved;
            localStorage.setItem("whs_job_data", JSON.stringify(resolved));
          }
        } catch (err) {
          console.error("Failed to resolve WHS token:", err);
        }
      }

      const activeCompId =
        initialJobData?.companyId ||
        initialJobData?.CompanyId ||
        location.state?.companyId ||
        localStorage.getItem("whs_company_id") ||
        loggedInUser?.companyId ||
        loggedInUser?.CompanyId ||
        "";
      if (isMounted) setCompanyId(activeCompId);
    };

    resolveContext();
    return () => {
      isMounted = false;
    };
  }, [token, jobData, loggedInUser?.CompanyId, loggedInUser?.companyId]);

  const activeUserId =
    location.state?.userId ||
    location.state?.userForm?.userId ||
    localStorage.getItem("whs_user_id") ||
    loggedInUser?.id ||
    loggedInUser?.userId ||
    null;

  // Lookups hook
  const {
    users,
    internalUsers,
    loadingJobUsers,
    statusOptions,
    loadingStatus,
    handleCreateStatus,
    handleEditStatus,
    handleDeleteStatus,
  } = useToolboxTalkLookups({
    companyId,
    userId: activeUserId,
    jobData,
    locationState: location.state,
    loggedInUser,
    presenter,
    setPresenter,
    status,
    setStatus,
  });

  // Document download/preview handler
  const handleDownload = async (doc) => {
    if (!doc) return;
    const docId = doc.Id || doc.id || doc.fileId || doc.ToolboxTalkDocumentId;
    const fileName = doc.FileName || doc.name || doc.fileName || "document";

    if (docId) {
      await downloadDocument(DocumentKeyEnum.ToolboxTalk, docId, fileName);
      return;
    }

    const filePathVal = doc.FilePath || doc.filePath || doc.path || doc.FileUrl || doc.fileUrl;
    if (filePathVal && typeof filePathVal === "string") {
      if (filePathVal.startsWith("http://") || filePathVal.startsWith("https://") || filePathVal.startsWith("blob:")) {
        const link = document.createElement("a");
        link.href = filePathVal;
        link.download = fileName;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      try {
        const previewUrl = await getAttachmentPreviewUrl(filePathVal);
        if (previewUrl) {
          const link = document.createElement("a");
          link.href = previewUrl;
          link.download = fileName;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (err) {
        console.error("Failed to download attachment:", err);
        toast.error("Failed to download document");
      }
    }
  };

  const handleReturnToAttendance = () => {
    const returnToken = token || paramToken || "";
    const activeUserId =
      location.state?.userId ||
      location.state?.userForm?.userId ||
      localStorage.getItem("whs_user_id") ||
      null;
    const activeCompId =
      companyId ||
      jobData?.companyId ||
      jobData?.CompanyId ||
      location.state?.companyId ||
      localStorage.getItem("whs_company_id") ||
      null;

    try {
      localStorage.setItem("whs_is_internal_user", "true");
    } catch (storageErr) {
      console.warn("Storage error:", storageErr);
    }
    navigate(returnToken ? `/whs/${returnToken}` : "/whs", {
      state: {
        jobData,
        companyId: activeCompId,
        token: returnToken,
        userId: activeUserId,
        isInternalUser: true,
        userForm: location.state?.userForm
          ? { ...location.state.userForm, isInternalUser: true }
          : undefined,
        activeCheckInDate: location.state?.activeCheckInDate,
        isCheckedIn: location.state?.isCheckedIn ?? true,
        workerStatus: location.state?.workerStatus || null,
        fromMenu: true,
      },
    });
  };

  // Auto-redirect timer on successful submission
  useEffect(() => {
    if (!isSubmitted) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleReturnToAttendance();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isSubmitted]);

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!activeJobId) {
      toast.error("Job site context is missing. Please return to Job Attendance.");
      return;
    }

    const newErrors = {};

    if (!presenter) {
      newErrors.presenter = "Please select the supervisor conducting the talk.";
    }

    if (!status) {
      newErrors.status = "Please select a status.";
    }

    if (!talkDate) {
      newErrors.talkDate = "Please select the date conducted.";
    }

    if (!time) {
      newErrors.time = "Please select the time conducted.";
    }

    if (!topic || !topic.trim()) {
      newErrors.topic = "Please enter a briefing topic.";
    }

    const plainDiscussion = stripHtml(discussionPoints);
    if (!plainDiscussion) {
      newErrors.discussionPoints = "Please enter the key discussion points covered.";
    } else if (plainDiscussion.length > 500) {
      newErrors.discussionPoints = "Discussion points exceed the maximum limit of 500 characters.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      const fieldOrder = [
        { key: "presenter", id: "field-presenter" },
        { key: "status", id: "field-status" },
        { key: "talkDate", id: "field-talkDate" },
        { key: "time", id: "field-time" },
        { key: "topic", id: "field-topic" },
        { key: "discussionPoints", id: "field-discussionPoints" },
      ];

      const firstFailed = fieldOrder.find((f) => newErrors[f.key]);
      if (firstFailed) {
        toast.error(newErrors[firstFailed.key]);

        setTimeout(() => {
          const targetEl = document.getElementById(firstFailed.id);
          if (targetEl) {
            targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
            const focusTarget = targetEl.querySelector(
              ".ProseMirror, [contenteditable='true'], input, textarea, button"
            );
            if (focusTarget) {
              focusTarget.focus();
            }
          }
        }, 80);
      }
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const attendeesList = selectedWorkers.map((worker) => {
        const userId = typeof worker === "object" && worker !== null ? worker.value || worker.userId : worker;
        return {
          UserId: userId,
          AttendanceConfirmed: true,
          SignatureUrl: null,
          SignedAt: new Date().toISOString(),
        };
      });

      let formattedTime = "07:00:00";
      if (time) {
        if (time.length === 5) {
          formattedTime = `${time}:00`;
        } else if (time.includes(":")) {
          formattedTime = time;
        }
      }

      const activeCompId =
        companyId ||
        jobData?.companyId ||
        jobData?.CompanyId ||
        loggedInUser?.companyId ||
        loggedInUser?.CompanyId ||
        null;

      const payload = {
        JobId: activeJobId,
        CompanyId: activeCompId,
        UserId: activeUserId,
        Topic: topic.trim(),
        TalkDate: talkDate,
        TalkTime: formattedTime,
        StatusId: status || null,
        DiscussionPoints: discussionPoints,
        Notes: notes?.trim() || null,
        SupervisorId: presenter,
        Attendees: attendeesList,
        Documents: docFiles,
      };

      const res = await createToolboxTalk(payload);

      if (res?.statusCode === 200 || res?.status === "Success" || res?.data) {
        const targetTalkId =
          typeof res.data === "string"
            ? res.data
            : res?.data?.Id ||
              res?.data?.id ||
              res?.data?.ToolboxTalkId ||
              res?.Id ||
              res?.id ||
              res?.ToolboxTalkId ||
              res?.data;

        if (targetTalkId && dynamicFieldsRef.current) {
          try {
            await dynamicFieldsRef.current.save(targetTalkId);
          } catch (dfErr) {
            console.error("Failed to save dynamic fields for toolbox talk:", dfErr);
          }
        }

        await invalidateToolboxTalksCache();

        const presenterName =
          internalUsers.find((u) => String(u.value).toLowerCase() === String(presenter).toLowerCase())?.label ||
          "Site Supervisor";

        setSubmittedSummary({
          jobTitle: jobData?.jobTitle || (jobData?.displayJobId ? `Job #${jobData.displayJobId}` : "Site Project"),
          topic: topic.trim(),
          presenterName,
          date: talkDate,
          time: time,
          attendeesCount: selectedWorkers.length,
        });

        setIsSubmitted(true);
        setCountdown(5);

        try {
          localStorage.setItem("whs_is_internal_user", "true");
        } catch (storageErr) {
          console.warn("Storage error:", storageErr);
        }

        toast.success("Toolbox talk recorded successfully!");
      } else {
        toast.error(res?.message || "Failed to create toolbox talk.");
      }
    } catch (err) {
      console.error("Error creating toolbox talk:", err);
      toast.error(
        err?.response?.data?.message || err?.message || "An error occurred while creating the toolbox talk."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const activeJobId = jobData?.jobId || jobData?.JobId;
  const isCheckedIn = Boolean(
    location.state?.isCheckedIn ||
    location.state?.activeCheckInDate ||
    localStorage.getItem("whs_is_checked_in") === "true"
  );
  const showSideMenu = isInternalUser && isCheckedIn;

  return (
    <WhsPageLayout
      title="Toolbox Talk"
      pageHeaderTitle="Toolbox Talk"
      subtitle="Site Safety & Check-in System"
      currentPage="toolbox-talk"
      token={token}
      jobData={jobData}
      userForm={location.state?.userForm}
      showSideMenu={showSideMenu}
      companyLogo={companyLogo}
      maxWidth="sm:max-w-2xl"
      modals={
        <ViewDocumentModal
          isOpen={isPreviewOpen}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewDoc(null);
          }}
          onDownload={handleDownload}
          document={previewDoc}
        />
      }
    >
      {isSubmitted ? (
        <WhsSuccessCountdown
          title="Toolbox Talk Recorded Successfully"
          description="Briefing summary and attendance sign-off have been registered on this job site."
          countdown={countdown}
          onReturn={handleReturnToAttendance}
          summaryCard={
            submittedSummary && (
              <div className="bg-slate-50/90 border border-slate-200/80 p-3.5 rounded-2xl text-left space-y-2.5 shadow-xs font-sans">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                    Job Site
                  </p>
                  <p className="text-xs font-bold text-slate-900">
                    {submittedSummary.jobTitle}
                  </p>
                </div>
                <div className="border-t border-slate-200/60 pt-2">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                    Briefing Topic
                  </p>
                  <p className="text-xs font-bold text-slate-800">
                    {submittedSummary.topic}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200/60">
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                      Conducted By
                    </p>
                    <p className="text-xs font-bold text-slate-700">
                      {submittedSummary.presenterName}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">
                      Workers Present
                    </p>
                    <p className="text-xs font-bold text-slate-700">
                      {submittedSummary.attendeesCount} worker(s)
                    </p>
                  </div>
                </div>
              </div>
            )
          }
        />
      ) : (
        <div className="space-y-4 font-sans">
          {/* Header Navigation & Add Fields Action */}
          <div className="flex items-center justify-between pb-3 border-b border-slate-200/60">
            <button
              type="button"
              onClick={handleReturnToAttendance}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors cursor-pointer"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span>Back to Attendance</span>
            </button>

            <button
              type="button"
              onClick={() => dynamicFieldsRef.current?.openAddFieldModal()}
              className="text-primary-600 hover:text-primary-700 hover:bg-primary-50 border border-primary-200 inline-flex cursor-pointer items-center gap-1 rounded-xl px-2.5 py-1 text-xs font-bold transition-all shadow-2xs"
            >
              <PlusCircleIcon className="h-3.5 w-3.5" />
              <span>Add Fields</span>
            </button>
          </div>

          {/* Job Site Context Banner */}
          <WhsJobBanner jobData={jobData} />

          {/* Form Fields Component */}
          <ToolboxTalkFormFields
            presenter={presenter}
            setPresenter={setPresenter}
            internalUsers={internalUsers}
            status={status}
            setStatus={setStatus}
            statusOptions={statusOptions}
            loadingStatus={loadingStatus}
            handleCreateStatus={handleCreateStatus}
            handleEditStatus={handleEditStatus}
            handleDeleteStatus={handleDeleteStatus}
            talkDate={talkDate}
            setTalkDate={setTalkDate}
            time={time}
            setTime={setTime}
            topic={topic}
            setTopic={setTopic}
            discussionPoints={discussionPoints}
            setDiscussionPoints={setDiscussionPoints}
            users={users}
            selectedWorkers={selectedWorkers}
            setSelectedWorkers={setSelectedWorkers}
            loadingJobUsers={loadingJobUsers}
            docFiles={docFiles}
            setDocFiles={setDocFiles}
            setPreviewDoc={setPreviewDoc}
            setIsPreviewOpen={setIsPreviewOpen}
            notes={notes}
            setNotes={setNotes}
            dynamicFieldsRef={dynamicFieldsRef}
            companyId={companyId}
            userId={activeUserId}
            loggedInUser={loggedInUser}
            errors={errors}
            setErrors={setErrors}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            onCancel={handleReturnToAttendance}
          />
        </div>
      )}
    </WhsPageLayout>
  );
}
